/**
 * VP-175 — Faza A wizarda „idiotoodporne reklamy na Meta".
 *
 * Prowadzi usera przez minimalny, idiotoodporny setup zanim w ogóle dotknie tworzenia
 * reklamy: (1) połącz konto Meta, (2) wybierz/utwórz Stronę FB, (3) wybierz/utwórz Konto
 * reklamowe. Czego Meta API NIE pozwala utworzyć programowo (Strona / Portfolio biznesowe /
 * Konto reklamowe) — pokazujemy deep-link do właściwego ekranu Meta + „Sprawdź ponownie".
 *
 * Reużywa istniejących endpointów (zero nowego backendu):
 *  - GET  /api/integrations/meta/status            — stan połączenia + konta reklamowe
 *  - GET  /api/integrations/meta/connect           — { url } do OAuth (popup)
 *  - POST /api/integrations/meta/select-ad-account — zapis wybranego konta
 *  - GET  /api/campaign-creator/pages              — { pages } Stron FB
 *
 * Po skompletowaniu (połączone + strona + konto) woła onReady — rodzic przechodzi do Fazy B.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "../ui/button";
import {
  CheckCircle2,
  Circle,
  Loader2,
  ExternalLink,
  RefreshCw,
  Link2,
  FileText,
  Wallet,
} from "lucide-react";
import { META_DEEPLINKS } from "./meta-deeplinks";
import SearchableCombobox from "./searchable-combobox";

export type MetaReadinessContext = {
  adAccountId: string;
  adAccountName?: string;
  pageId: string;
  pageName?: string;
};

type Props = { onReady: (ctx: MetaReadinessContext) => void };

type MetaStatus = {
  connected?: boolean;
  tokenExpired?: boolean;
  selectedAdAccountId?: string | null;
  selectedAdAccountName?: string | null;
  adAccounts?: { id: string; name: string; accountStatus?: number; currency?: string }[];
};

type MetaPage = { id: string; name: string };

const STATUS_KEY = ["/api/integrations/meta/status"] as const;
const PAGES_KEY = ["campaign-creator-pages"] as const;

/** Otwiera OAuth Meta w popupie (wzorzec z analytics-settings-modal) i odświeża status po zamknięciu. */
function openMetaOAuthPopup(onClosed: () => void): void {
  const w = 600,
    h = 700;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(
    "about:blank",
    "meta-oauth",
    `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
  );
  void apiRequest<{ url?: string }>("GET", "/api/integrations/meta/connect")
    .then((data) => {
      if (!data?.url) {
        popup?.close();
        return;
      }
      if (popup && !popup.closed) popup.location.href = data.url;
      else window.location.href = data.url;
    })
    .catch(() => {
      popup?.close();
    });
  if (popup) {
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        onClosed();
      }
    }, 500);
  }
}

function StepRow({
  index,
  title,
  done,
  active,
  children,
}: {
  index: number;
  title: string;
  done: boolean;
  active: boolean;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        active
          ? "border-[#ef3e29]/40 bg-[#ef3e29]/[0.04]"
          : done
            ? "border-emerald-500/30 bg-emerald-500/[0.03]"
            : "border-white/[0.08] bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center gap-2.5">
        {done ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
        ) : (
          <Circle className={`w-5 h-5 shrink-0 ${active ? "text-[#ef3e29]" : "text-white/30"}`} />
        )}
        <span className="text-[11px] font-mono text-white/40">Krok {index}</span>
        <span className="text-sm font-medium text-white">{title}</span>
      </div>
      {children && <div className="mt-3 pl-7">{children}</div>}
    </div>
  );
}

export default function MetaReadiness({ onReady }: Props): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: status,
    isLoading: statusLoading,
    isFetching: statusFetching,
  } = useQuery<MetaStatus>({
    queryKey: STATUS_KEY,
    queryFn: () => apiRequest("GET", "/api/integrations/meta/status"),
  });
  const connected = !!status?.connected && !status?.tokenExpired;

  const { data: pagesData, isFetching: pagesFetching } = useQuery<{ pages?: MetaPage[] }>({
    queryKey: PAGES_KEY,
    queryFn: () => apiRequest("GET", "/api/campaign-creator/pages"),
    enabled: connected,
  });
  // `?? []` tworzy nowa tablice przy kazdym renderze, a obie trafiaja do zaleznosci
  // useMemo nizej — bez tego przeliczaly sie w kolko (VP-326).
  const pages = useMemo(() => pagesData?.pages ?? [], [pagesData]);
  const adAccounts = useMemo(() => status?.adAccounts ?? [], [status]);

  const [pageId, setPageId] = useState("");
  const [adAccountId, setAdAccountId] = useState("");
  const [savingAccount, setSavingAccount] = useState(false);

  // Domyślny wybór: pierwsza strona / konto wybrane wcześniej w integracji.
  // Uwaga: bierzemy wcześniej wybrane konto TYLKO jeśli wciąż jest na liście dostępnych
  // (selectedAdAccountId potrafi być nieaktualny — np. konto usunięte/odebrane w Meta).
  const effectivePageId = pageId || (pages.length === 1 ? pages[0].id : "");
  const knownSelectedAdAccountId = adAccounts.some((a) => a.id === status?.selectedAdAccountId)
    ? (status?.selectedAdAccountId ?? "")
    : "";
  const effectiveAdAccountId =
    adAccountId || knownSelectedAdAccountId || (adAccounts.length === 1 ? adAccounts[0].id : "");

  const ready = connected && !!effectivePageId && !!effectiveAdAccountId;
  const selectedPage = useMemo(
    () => pages.find((p) => p.id === effectivePageId),
    [pages, effectivePageId]
  );
  const selectedAccount = useMemo(
    () => adAccounts.find((a) => a.id === effectiveAdAccountId),
    [adAccounts, effectiveAdAccountId]
  );

  const refetchAll = () => {
    void queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
  };

  const handleConnect = () => openMetaOAuthPopup(refetchAll);

  const handleConfirmAccount = async () => {
    if (!effectiveAdAccountId) return;
    setSavingAccount(true);
    try {
      await apiRequest("POST", "/api/integrations/meta/select-ad-account", {
        adAccountId: effectiveAdAccountId,
        adAccountName: selectedAccount?.name,
      });
      await queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    } catch (err) {
      toast({
        title: "Nie udało się zapisać konta",
        description: err instanceof Error ? err.message : "Spróbuj ponownie.",
        variant: "destructive",
      });
    } finally {
      setSavingAccount(false);
    }
  };

  const handleContinue = () => {
    if (!ready) return;
    void handleConfirmAccount().then(() => {
      onReady({
        adAccountId: effectiveAdAccountId,
        adAccountName: selectedAccount?.name,
        pageId: effectivePageId,
        pageName: selectedPage?.name,
      });
    });
  };

  if (statusLoading) {
    return (
      <div className="flex items-center gap-2 text-white/50 text-sm p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Sprawdzam połączenie z Meta…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Krok 1 — połącz Meta */}
      <StepRow index={1} title="Połącz konto Meta" done={connected} active={!connected}>
        {connected ? (
          <p className="text-xs text-emerald-300/80">Konto Meta połączone.</p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-white/50">
              Zaloguj się do Facebooka i nadaj uprawnienia — to pozwoli tworzyć i uruchamiać
              reklamy.
            </p>
            <Button
              size="sm"
              onClick={handleConnect}
              className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white"
            >
              <Link2 className="w-3.5 h-3.5 mr-1.5" /> Połącz z Meta
            </Button>
          </div>
        )}
      </StepRow>

      {/* Krok 2 — strona FB */}
      <StepRow
        index={2}
        title="Wybierz stronę na Facebooku"
        done={connected && !!effectivePageId}
        active={connected && !effectivePageId}
      >
        {!connected ? (
          <p className="text-xs text-white/30">Najpierw połącz konto Meta.</p>
        ) : pages.length === 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-white/50">
              Nie znaleźliśmy żadnej strony. Utwórz ją na Facebooku, a potem kliknij „Sprawdź
              ponownie".
            </p>
            <div className="flex gap-2">
              <a href={META_DEEPLINKS.createPage} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="border-white/15">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Utwórz stronę{" "}
                  <ExternalLink className="w-3 h-3 ml-1.5" />
                </Button>
              </a>
              <Button size="sm" variant="ghost" onClick={refetchAll} disabled={pagesFetching}>
                <RefreshCw
                  className={`w-3.5 h-3.5 mr-1.5 ${pagesFetching ? "animate-spin" : ""}`}
                />{" "}
                Sprawdź ponownie
              </Button>
            </div>
          </div>
        ) : (
          <SearchableCombobox
            options={pages.map((p) => ({ id: p.id, label: p.name }))}
            value={effectivePageId}
            onSelect={setPageId}
            placeholder="Wybierz stronę…"
            searchPlaceholder="Szukaj strony…"
            emptyText="Nie znaleziono strony o tej nazwie."
          />
        )}
      </StepRow>

      {/* Krok 3 — konto reklamowe */}
      <StepRow
        index={3}
        title="Wybierz konto reklamowe"
        done={connected && !!effectiveAdAccountId}
        active={connected && !!effectivePageId && !effectiveAdAccountId}
      >
        {!connected ? (
          <p className="text-xs text-white/30">Najpierw połącz konto Meta.</p>
        ) : adAccounts.length === 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-white/50">
              Brak konta reklamowego. Utwórz je w Menedżerze Firmy (potrzebne też Portfolio
              biznesowe), a potem „Sprawdź ponownie".
            </p>
            <div className="flex flex-wrap gap-2">
              <a href={META_DEEPLINKS.createBusiness} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="border-white/15">
                  Portfolio biznesowe <ExternalLink className="w-3 h-3 ml-1.5" />
                </Button>
              </a>
              <a href={META_DEEPLINKS.createAdAccount} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="border-white/15">
                  <Wallet className="w-3.5 h-3.5 mr-1.5" /> Utwórz konto reklamowe{" "}
                  <ExternalLink className="w-3 h-3 ml-1.5" />
                </Button>
              </a>
              <Button size="sm" variant="ghost" onClick={refetchAll} disabled={statusFetching}>
                <RefreshCw
                  className={`w-3.5 h-3.5 mr-1.5 ${statusFetching ? "animate-spin" : ""}`}
                />{" "}
                Sprawdź ponownie
              </Button>
            </div>
          </div>
        ) : (
          <SearchableCombobox
            options={adAccounts.map((a) => ({ id: a.id, label: a.name, hint: a.currency }))}
            value={effectiveAdAccountId}
            onSelect={setAdAccountId}
            placeholder="Wybierz konto reklamowe…"
            searchPlaceholder="Szukaj konta…"
            emptyText="Nie znaleziono konta o tej nazwie."
          />
        )}
      </StepRow>

      <div className="flex justify-end pt-1">
        <Button
          onClick={handleContinue}
          disabled={!ready || savingAccount}
          className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white disabled:opacity-40"
        >
          {savingAccount ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
          Dalej — stwórz reklamę
        </Button>
      </div>
    </div>
  );
}
